import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, CalendarClock, ChevronRight, Flag, SlidersHorizontal } from "lucide-react";

// Featured countdown target: Raduno Auto Storiche Tedesche — R.A.S.T. · Verucchio (7 Nov 2026).
// Identify by stable attributes (date + name/location) — NOT by DB id, which differs between preview and production.
const isVerucchio = (r) =>
  r.date === "2026-11-07" &&
  /verucchio|r\.?a\.?s\.?t/i.test(`${r.name || ""} ${r.location || ""}`);

// Romagna box around Rimini (44.06, 12.57) / Cesena (44.14, 12.24)
const inBox = (r) =>
  r.lat != null && r.lng != null &&
  r.lat >= 43.75 && r.lat <= 44.35 && r.lng >= 11.85 && r.lng <= 12.85;
const isUpcoming = (d) => new Date(d) >= new Date(new Date().toDateString());

function useCountdown(targetIso) {
  const calc = () => {
    if (!targetIso) return null;
    const ms = Math.max(0, new Date(`${targetIso}T09:00:00`) - new Date());
    return {
      d: Math.floor(ms / 86400000),
      h: Math.floor((ms % 86400000) / 3600000),
      m: Math.floor((ms % 3600000) / 60000),
      s: Math.floor((ms % 60000) / 1000),
    };
  };
  const [t, setT] = useState(calc());
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [targetIso]);
  return t;
}

const Cell = ({ v, label }) => (
  <div className="flex flex-col items-center">
    <div className="rounded-sm bg-white/15 backdrop-blur px-2.5 sm:px-3 py-1.5 min-w-[46px] sm:min-w-[54px]">
      <span className="block font-display font-black text-xl sm:text-2xl tabular-nums text-white">{String(v).padStart(2, "0")}</span>
    </div>
    <span className="text-[9px] font-mono uppercase tracking-widest text-white/70 mt-1">{label}</span>
  </div>
);

export default function NearbyEvents({ races = [] }) {
  const [disc, setDisc] = useState("Tutti");

  const nearby = races
    .filter((r) => inBox(r) && isUpcoming(r.date))
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  const disciplines = ["Tutti", ...Array.from(new Set(nearby.map((r) => r.discipline).filter(Boolean)))];
  const counts = nearby.reduce((acc, r) => {
    if (r.discipline) acc[r.discipline] = (acc[r.discipline] || 0) + 1;
    return acc;
  }, {});

  const filtered = disc === "Tutti" ? nearby : nearby.filter((r) => r.discipline === disc);
  const featured =
    disc === "Tutti"
      ? nearby.find(isVerucchio) || nearby[0]
      : filtered[0];
  const cd = useCountdown(featured?.date);

  if (nearby.length === 0) return null;
  const rest = (featured ? filtered.filter((r) => r.id !== featured.id) : filtered).slice(0, 6);

  return (
    <section data-testid="nearby-events" className="max-w-7xl mx-auto px-4 mt-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-6 w-1.5 bg-legend-red" />
        <h2 className="font-display font-black uppercase text-2xl tracking-wide">Prossimi eventi vicino Rimini / Cesena</h2>
      </div>

      {/* Filter window */}
      <div data-testid="nearby-filters" className="mb-5 rounded-sm border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-white/5 p-3">
        <div className="flex items-center gap-2 mb-2.5">
          <SlidersHorizontal size={14} className="text-legend-red" />
          <span className="font-mono uppercase text-[11px] tracking-widest text-slate-500 dark:text-slate-400">Filtra per disciplina</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {disciplines.map((d) => (
            <button
              key={d}
              onClick={() => setDisc(d)}
              data-testid={`nearby-filter-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wide transition-colors ${
                disc === d
                  ? "bg-legend-red text-white"
                  : "bg-white dark:bg-legend-paper border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-legend-red"
              }`}
            >
              {d}
              <span className="ml-1.5 opacity-70">{d === "Tutti" ? nearby.length : counts[d] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-5">
        {/* Featured countdown card (clickable → calendar) */}
        {featured && (
          <Link
            to={`/calendario?race=${featured.id}`}
            data-testid="nearby-featured-countdown"
            className="group relative overflow-hidden rounded-sm bg-gradient-to-br from-legend-red to-legend-dark text-white p-6 flex flex-col justify-between hover:shadow-xl transition-all"
          >
            <div className="checkered absolute inset-y-0 right-0 w-28 opacity-10" />
            <div className="relative">
              <span className="inline-flex items-center gap-1.5 bg-legend-yellow text-legend-ink font-mono uppercase text-[10px] tracking-widest px-2 py-0.5 rounded-sm">
                <Flag size={12} /> Prossimo evento in Romagna
              </span>
              <h3 className="font-display font-black uppercase text-2xl md:text-3xl leading-tight mt-3">{featured.name}</h3>
              <p className="mt-1 text-sm text-white/80 flex items-center gap-1.5"><MapPin size={14} /> {featured.location}</p>
              <p className="mt-1 text-sm font-semibold text-legend-yellow flex items-center gap-1.5"><CalendarClock size={14} /> {featured.date_label || featured.date}</p>
            </div>
            {cd && (
              <div className="relative mt-5 flex items-center gap-2 sm:gap-3">
                <Cell v={cd.d} label="giorni" />
                <Cell v={cd.h} label="ore" />
                <Cell v={cd.m} label="min" />
                <Cell v={cd.s} label="sec" />
                <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide bg-white/15 group-hover:bg-white/25 px-3 py-2 rounded-sm transition-colors">
                  Vedi nel calendario <ChevronRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            )}
          </Link>
        )}

        {/* List of other nearby events */}
        <div className="rounded-sm border border-slate-200 dark:border-slate-800 bg-white dark:bg-legend-paper divide-y divide-slate-100 dark:divide-slate-800">
          {rest.length === 0 && <p className="p-5 text-sm text-slate-500">Nessun altro evento per questo filtro.</p>}
          {rest.map((r) => (
            <Link
              key={r.id}
              to={`/calendario?race=${r.id}`}
              data-testid={`nearby-event-${r.id}`}
              className="flex items-start gap-3 p-4 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
            >
              <div className="text-center shrink-0 w-12">
                <p className="font-display font-black text-xl leading-none text-legend-red">{new Date(r.date).getDate()}</p>
                <p className="text-[10px] font-mono uppercase text-slate-500">{new Date(r.date).toLocaleDateString("it-IT", { month: "short" })}</p>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-display font-bold uppercase leading-tight text-slate-900 dark:text-white truncate">{r.name}</h4>
                <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={11} /> {r.location}</p>
              </div>
              <ChevronRight size={16} className="text-slate-400 mt-1 shrink-0" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
